import { animate, motion } from "framer-motion";
import { useState, useEffect } from "react";

interface CircularProgressRingProps {
  progress?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
  showPercentage?: boolean;
  animated?: boolean;
  duration?: number;
  delay?: number;
}

const CircularProgressRing: React.FC<CircularProgressRingProps> = ({
  progress = 0,
  size = 120,
  strokeWidth = 8,
  color = "#3b82f6",
  backgroundColor = "#e5e7eb",
  showPercentage = true,
  animated = true,
  duration = 2,
  delay = 1,
}) => {
  // States definition
  const [displayProgress, setDisplayProgress] = useState(
    animated ? 0 : progress
  );

  // Calculate circle properties
  const center = size / 2;
  const radius = center - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;

  // Calculate stroke dash offset based on progress
  const strokeDashoffset =
    circumference - (displayProgress / 100) * circumference;

    
  // Animate progress on mount or when progress changes
  useEffect(() => {
    const controls = animate(0, progress, {
      duration: duration,
      ease: "circInOut",
      delay: delay,
      onUpdate: (latest) => setDisplayProgress(latest)
    });

    return () => controls.stop();
  }, [progress]);

  return (
    <div className="inline-block relative">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
        />

        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>

      {/* Percentage text */}
      {showPercentage && (
        <div className="absolute inset-0 inline-flex items-center justify-center text-xs text-center text-neutral-600 select-none">
          {Math.round(displayProgress)}
        </div>
      )}
    </div>
  );
};

export default CircularProgressRing;
